import { useState } from 'react';

import { gdprApi } from '@/api/queries/gdpr';
import { Button, Icon, Input, Modal, useToast, type IconName } from '@/components/ui';

type Op = 'resend' | 'export' | 'dsar' | 'rtbf' | 'renewal' | 'retention';

interface OpDef {
  id: Op;
  icon: IconName;
  title: string;
  description: string;
  destructive?: boolean;
}

const OPS: OpDef[] = [
  { id: 'resend', icon: 'send', title: 'Bulk resend renewal emails', description: 'Pick consents from the list, then use the bulk action bar.' },
  { id: 'export', icon: 'download', title: 'Export consent log', description: 'Download a CSV of every consent and audit entry.' },
  { id: 'dsar', icon: 'eye', title: 'Generate DSAR package', description: 'Build a Data Subject Access Request export for one contact.' },
  { id: 'rtbf', icon: 'trash', title: 'Right to be forgotten', description: 'Anonymise a contact and every consent referencing it.', destructive: true },
  { id: 'renewal', icon: 'refresh', title: 'Run renewal sweep', description: 'Mark consents entering the 30-day window as expiring.' },
  { id: 'retention', icon: 'shield', title: 'Retention policy', description: 'Withdrawn consents are anonymised after 3 years.' },
];

interface Props {
  onClose: () => void;
}

export function GdprOperationsModal({ onClose }: Props) {
  const [selected, setSelected] = useState<Op | null>(null);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="GDPR operations"
      subtitle="Compliance tasks under EU 2016/679."
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {!selected ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
          }}
        >
          {OPS.map((op) => (
            <button
              key={op.id}
              type="button"
              onClick={() => setSelected(op.id)}
              style={{
                textAlign: 'left',
                padding: 14,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--surface-1)',
                cursor: 'pointer',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  background: op.destructive ? '#fee2e2' : 'var(--brand-soft)',
                  color: op.destructive ? '#b91c1c' : 'var(--brand)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name={op.icon} size={14} />
              </span>
              <div>
                <div style={{ fontWeight: 600 }}>{op.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  {op.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <OperationPanel op={selected} onBack={() => setSelected(null)} onDone={onClose} />
      )}
    </Modal>
  );
}

function OperationPanel({
  op,
  onBack,
  onDone,
}: {
  op: Op;
  onBack: () => void;
  onDone: () => void;
}) {
  const dsar = gdprApi.useBuildDSAR();
  const rtbf = gdprApi.useRTBF();
  const renewal = gdprApi.useRunRenewalJob();
  const toast = useToast();
  const [contactId, setContactId] = useState('');

  const runDSAR = async () => {
    try {
      const res = await dsar.mutateAsync(contactId);
      toast.show({ severity: 'success', title: `DSAR job queued — ${res.jobId}` });
      onDone();
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed',
      });
    }
  };
  const runRTBF = async () => {
    if (!confirm('Anonymise this contact and every consent referencing it. This is irreversible. Continue?')) return;
    try {
      const res = await rtbf.mutateAsync(contactId);
      toast.show({ severity: 'success', title: `RTBF job queued — ${res.jobId}` });
      onDone();
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed',
      });
    }
  };
  const runRenewal = async () => {
    try {
      const res = await renewal.mutateAsync();
      toast.show({
        severity: 'success',
        title: `Marked ${res.markedExpiring} consents as expiring`,
      });
      onDone();
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Renewal sweep failed',
      });
    }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" leftIcon={<Icon name="chevronLeft" size={12} />} onClick={onBack}>
        Back
      </Button>
      <div style={{ marginTop: 12 }}>
        {op === 'resend' && (
          <p>
            Close this modal and use the row checkboxes to select consents. The bulk action bar
            will appear at the top of the list with a <strong>Bulk resend</strong> button.
          </p>
        )}
        {op === 'export' && (
          <div>
            <p>Download every consent and its audit entries as CSV.</p>
            <a
              className="ds-btn ds-btn-primary"
              href={gdprApi.buildLogExportURL()}
              style={{ textDecoration: 'none' }}
            >
              <Icon name="download" size={14} /> Download consent-log.csv
            </a>
          </div>
        )}
        {op === 'dsar' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <p>Build a Data Subject Access Request package for one contact.</p>
            <Input
              placeholder="Contact ID"
              value={contactId}
              onChange={(e) => setContactId(e.currentTarget.value)}
            />
            <Button variant="primary" loading={dsar.isPending} onClick={runDSAR}>
              Queue DSAR job
            </Button>
            <p style={{ fontSize: 12, color: 'var(--text-3)' }}>
              The export lands in MinIO; poll /sync-jobs/&lt;id&gt; for the download URL.
            </p>
          </div>
        )}
        {op === 'rtbf' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <p style={{ color: '#b91c1c' }}>
              <Icon name="alert" size={12} /> This is irreversible. PII is stripped from the
              contact and every consent referencing it. Analytics aggregates are preserved.
            </p>
            <Input
              placeholder="Contact ID"
              value={contactId}
              onChange={(e) => setContactId(e.currentTarget.value)}
            />
            <Button variant="danger" loading={rtbf.isPending} onClick={runRTBF}>
              Queue RTBF job
            </Button>
          </div>
        )}
        {op === 'renewal' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <p>
              Idempotent sweep that flags consents entering the 30-day window as{' '}
              <strong>expiring</strong>. Intended for nightly cron, currently triggered manually.
            </p>
            <Button variant="primary" loading={renewal.isPending} onClick={runRenewal}>
              Run renewal sweep now
            </Button>
          </div>
        )}
        {op === 'retention' && (
          <div>
            <p>
              <strong>Policy:</strong> Withdrawn consents are kept for 3 years for audit, then
              anonymised by a background sweep. This is fixed in code; contact engineering to
              change the window.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
